import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BACKGROUND_REFETCH_MS, queryKeys } from '@/api/queryKeys'
import * as prioritiesApi from '@/api/priorities'
import type { Priority, PriorityHistory } from '@/types'

/** Active (non-archived) priorities in a bucket, sorted by rank. */
export function usePrioritiesByBucket(bucketId: string | undefined): Priority[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.priorities.byBucket(bucketId),
    queryFn: () => prioritiesApi.fetchPrioritiesByBucket(bucketId!),
    enabled: !!bucketId,
    refetchOnWindowFocus: true,
    refetchInterval: BACKGROUND_REFETCH_MS,
  })
  return bucketId ? data : []
}

/** A single priority by ID, or undefined while loading / not found. */
export function usePriority(id: string | null): Priority | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.priorities.detail(id),
    queryFn: () => prioritiesApi.fetchPriority(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  })
  return data ?? undefined
}

/** All archived priorities, newest-archived first. */
export function useArchivedPriorities(): Priority[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.priorities.archived(),
    queryFn: prioritiesApi.fetchArchivedPriorities,
    refetchOnWindowFocus: true,
  })
  return data
}

/** All non-archived, non-completed priorities across all buckets, by rank. */
export function useAllActivePriorities(): Priority[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.priorities.allActive(),
    queryFn: prioritiesApi.fetchAllActivePriorities,
    refetchOnWindowFocus: true,
    refetchInterval: BACKGROUND_REFETCH_MS,
  })
  return data
}

/** Change history for a priority, newest first. */
export function usePriorityHistory(priorityId: string | null): PriorityHistory[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.priorities.history(priorityId),
    queryFn: () => prioritiesApi.fetchPriorityHistory(priorityId!),
    enabled: !!priorityId,
  })
  return priorityId ? data : []
}

function invalidatePriorities(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['priorities'] })
}

export function useCreatePriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ bucketId, title, extra }: {
      bucketId: string; title: string; extra?: Parameters<typeof prioritiesApi.createPriority>[2]
    }) => prioritiesApi.createPriority(bucketId, title, extra),
    onSuccess: () => invalidatePriorities(qc),
  })
}

export function useUpdatePriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof prioritiesApi.updatePriority>[1] }) =>
      prioritiesApi.updatePriority(id, data),
    onSuccess: () => invalidatePriorities(qc),
  })
}

export function useSetPriorityStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, releaseNote }: { id: string; status: Priority['status']; releaseNote?: string }) =>
      prioritiesApi.setStatus(id, status, releaseNote),
    onSuccess: () => {
      invalidatePriorities(qc)
      void qc.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useReorderPriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, bucketId, beforeRank, afterRank }: {
      id: string; bucketId: string; beforeRank: number | null; afterRank: number | null
    }) => prioritiesApi.reorderPriority(id, bucketId, beforeRank, afterRank),
    onSuccess: () => invalidatePriorities(qc),
  })
}

export function useArchivePriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: prioritiesApi.archivePriority,
    onSuccess: () => invalidatePriorities(qc),
  })
}

export function useMarkPriorityReviewed() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: prioritiesApi.markPriorityReviewed,
    onSuccess: () => invalidatePriorities(qc),
  })
}

export function useRestorePriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: prioritiesApi.restorePriority,
    onSuccess: () => invalidatePriorities(qc),
  })
}

export function useDeletePriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: prioritiesApi.permanentlyDeletePriority,
    onSuccess: () => invalidatePriorities(qc),
  })
}

export function useDuplicatePriority() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: prioritiesApi.duplicatePriority,
    onSuccess: () => invalidatePriorities(qc),
  })
}
